/**
 * srcset variants for an already-proxied Tebex image URL.
 *
 * The catalogue stores each thumbnail as a single proxied URL pinned to
 * w=1280, and every surface was downloading that: a card the grid renders
 * at 427 CSS pixels was decoding three times the pixels it could show.
 * The image proxy already accepts a w parameter with a fixed menu of
 * widths, so the variants are just the same URL with w swapped — the
 * browser picks by layout, and nothing new is asked of the worker.
 *
 * Anything that is not a proxied URL (local /img assets, absolute
 * fallbacks) is returned untouched: srcSet undefined, plain src only.
 */

const PROXY_MARKER = '/api/tebex/image?';
const HERO_MARKER = '/img/hero/';

/**
 * srcset variants for the hero carousel's own artwork.
 *
 * These files are local rather than proxied, so they never went through the
 * catalogue's srcset and every screen was handed the 1600px original — a
 * 318px slot on a phone was being filled with a 239KB image. The variants sit
 * beside the original as `<name>-800w.webp` and `<name>-480w.webp`; 800
 * covers a phone at 2x and a 1440 desktop at 1x, and the original stays for
 * dense desktop screens.
 *
 * Anything that is not one of these local files is returned untouched.
 */
export const heroSrcSet = (src) => {
  if (typeof src !== 'string' || !src.startsWith(HERO_MARKER) || !src.endsWith('.webp')) {
    return undefined;
  }
  const base = src.slice(0, -'.webp'.length);
  return `${base}-480w.webp 480w, ${base}-800w.webp 800w, ${src} 1600w`;
};

/* What the slot is actually worth: one card owns most of a phone, and a bit
   under half of anything wider. Kept in step with .angled-card { width: … }. */
export const HERO_SIZES = '(max-width: 768px) 82vw, 48vw';

/**
 * The same proxied image at a named width.
 *
 * For pictures that are never seen sharp. The hero's backdrop is the centred
 * product's art blurred by 46px, dimmed and pushed past the edges — and it
 * was being fetched at the catalogue's full 1280, which came to 253KB of two
 * images on the home page that nobody can resolve a pixel of. The proxy takes
 * a width already; this only asks it for less.
 */
export const tebexAtWidth = (src, width) => {
  if (typeof src !== 'string' || !src.includes(PROXY_MARKER) || !src.includes('w=')) {
    return src;
  }
  return src.replace(/([?&]w=)\d+/, `$1${width}`);
};

/* The smallest width the proxy offers. Behind 46px of blur it is still more
   detail than survives. */
export const BACKDROP_WIDTH = 480;

export const tebexSrcSet = (src, widths = [480, 640, 960, 1280]) => {
  if (typeof src !== 'string' || !src.includes(PROXY_MARKER) || !src.includes('w=')) {
    return undefined;
  }
  return widths
    .map((w) => `${src.replace(/([?&]w=)\d+/, `$1${w}`)} ${w}w`)
    .join(', ');
};
